import { MapPin, Star, Globe } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { BrutalButton } from '@/components/ui/BrutalButton';

interface MapMarkerPopupProps {
  professional: {
    id: string;
    slug: string;
    business_name?: string;
    first_name?: string;
    last_name?: string;
    address_city?: string;
    address_region?: string;
    profile_image?: string;
    is_verified?: boolean;
    is_company?: boolean;
    average_rating?: number;
    review_count?: number;
    languages_spoken?: string[];
    subscription_type?: 'base' | 'pro' | 'plus' | null;
    tagline_it?: string;
    tagline_en?: string;
    tagline_es?: string;
  };
  categoryNames: string[];
  onNavigate: (path: string) => void;
  currentLanguage: 'it' | 'en' | 'es';
  translations: {
    viewProfile: string;
    viewCompany: string;
    companyBadge: string;
  };
}

export const MapMarkerPopup = ({ professional, categoryNames, onNavigate, currentLanguage, translations }: MapMarkerPopupProps) => {
  
  // Explicit check: only true if is_company === true
  const isCompany = professional.is_company === true;
  
  const displayName = isCompany 
    ? professional.business_name
    : (professional.first_name && professional.last_name 
        ? `${professional.first_name} ${professional.last_name.charAt(0)}.` 
        : professional.business_name);
  
  const handleClick = () => {
    if (isCompany) {
      onNavigate(`/company/${professional.slug}`);
    } else {
      onNavigate(`/professional/${professional.slug}`);
    }
  };
  
  return (
    <div className="w-[280px] bg-card rounded-lg overflow-hidden">
      <div className="p-4 space-y-3">
        {/* Header: Image + Name */}
        <div className="flex gap-3">
          {/* Image 100x100 */}
          {professional.profile_image ? (
            <img
              src={professional.profile_image}
              alt={professional.business_name || 'Professional'}
              className="w-[100px] h-[100px] object-cover rounded-full flex-shrink-0 border-4 border-dark"
            />
          ) : (
            <div className="w-[100px] h-[100px] bg-muted flex items-center justify-center rounded-full flex-shrink-0 border-4 border-dark">
              <span className="text-3xl">
                {professional.business_name ? professional.business_name[0] : '?'}
              </span>
            </div>
          )}
          
          {/* Name and Badges */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-1.5 flex-wrap">
              <h3 className="font-heading font-bold text-base leading-tight">
                {displayName}
              </h3>
              {isCompany && (
                <Badge className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[10px] px-1.5 py-0.5 border-0">
                  {translations.companyBadge}
                </Badge>
              )}
              {!isCompany && professional.subscription_type === 'plus' && (
                <Badge className="bg-purple-500 text-white text-[10px] px-1.5 py-0.5 border-0">
                  PLUS
                </Badge>
              )}
              {!isCompany && professional.subscription_type === 'pro' && (
                <Badge className="bg-blue-500 text-white text-[10px] px-1.5 py-0.5 border-0">
                  PRO
                </Badge>
              )}
            </div>
            {!isCompany && professional.first_name && professional.last_name && professional.business_name && (
              <p className="text-sm text-muted-foreground truncate mt-1">
                {professional.business_name}
              </p>
            )}
            {isCompany && (professional.tagline_it || professional.tagline_en || professional.tagline_es) && (
              <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                {currentLanguage === 'it' 
                  ? professional.tagline_it 
                  : currentLanguage === 'en' 
                    ? (professional.tagline_en || professional.tagline_it)
                    : (professional.tagline_es || professional.tagline_it)}
              </p>
            )}
          </div>
        </div>

        {/* Categories */}
        {categoryNames.length > 0 && (
          <div className="text-xs text-muted-foreground">
            {categoryNames.join(', ')}
          </div>
        )}

        <div className="text-sm space-y-2">
          {/* Region */}
          {professional.address_region && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{professional.address_region}</span>
            </div>
          )}

          {/* Rating */}
          <div className="flex items-center gap-1.5">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={`w-4 h-4 ${
                  star <= Math.round(professional.average_rating || 0)
                    ? 'fill-dark text-dark'
                    : 'fill-none text-dark stroke-[1.5]'
                }`}
              />
            ))}
            <span className="text-xs text-muted-foreground ml-1">
              ({professional.review_count || 0})
            </span>
          </div>

          {/* Languages */}
          {professional.languages_spoken && professional.languages_spoken.length > 0 && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Globe className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">
                {professional.languages_spoken.slice(0, 3).join(', ')}
                {professional.languages_spoken.length > 3 && ` +${professional.languages_spoken.length - 3}`}
              </span>
            </div>
          )}
        </div>

        {/* CTA Button */}
        <BrutalButton 
          size="sm" 
          className="w-full"
          onClick={handleClick}
        >
          {isCompany ? translations.viewCompany : translations.viewProfile}
        </BrutalButton>
      </div>
    </div>
  );
};
